import { useState, useEffect, useCallback, useMemo } from "react";
import { MessageReaction } from "../component/message-reactions";

// Define request options type
interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

export interface ReactionsApiConfig {
  // API endpoints
  getEndpoint: string;
  addEndpoint: string;
  removeEndpoint: string;

  // Request configuration
  buildGetUrl?: (messageId: string) => string;
  buildAddRequest?: (data: ReactionRequestData) => {
    url: string;
    options: FetchOptions;
  };
  buildRemoveUrl?: (data: ReactionRequestData) => string;

  // Response parsing
  parseGetResponse?: (response: unknown) => { reactions: MessageReaction[] } | null;
}

export interface ReactionRequestData {
  participantId: string;
  messageId: string;
  threadId: string;
  emoji: string;
}

interface UseReactionsProps {
  messageId: string;
  participantId: string;
  threadId: string;
  // apiConfig: ReactionsApiConfig;
  httpUrl: string;
  wsUrl: string;
  initialReactions?: MessageReaction[];
  extraHeaders?: Record<string, string>;
}

export function useReactions({
  messageId,
  participantId,
  threadId,
  // apiConfig,
  httpUrl,
  // wsUrl,
  initialReactions = [],
  extraHeaders,
}: UseReactionsProps) {
  const [reactions, setReactions] = useState<MessageReaction[]>(initialReactions);
  const [isLoading, setIsLoading] = useState(false);

  const apiConfig = useMemo(() => createLocalReactionsConfig(httpUrl), [httpUrl]);

  // Build API URLs using provided config or defaults
  const buildGetUrl = useCallback(
    (msgId: string) => {
      if (apiConfig.buildGetUrl) {
        return apiConfig.buildGetUrl(msgId);
      }
      return `${apiConfig.getEndpoint}?messageId=${msgId}`;
    },
    [apiConfig]
  );

  const buildAddRequest = useCallback(
    (data: ReactionRequestData) => {
      if (apiConfig.buildAddRequest) {
        return apiConfig.buildAddRequest(data);
      }
      return {
        url: apiConfig.addEndpoint,
        options: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      };
    },
    [apiConfig]
  );

  const buildRemoveUrl = useCallback(
    (data: ReactionRequestData) => {
      if (apiConfig.buildRemoveUrl) {
        return apiConfig.buildRemoveUrl(data);
      }
      return `${apiConfig.removeEndpoint}?participantId=${data.participantId}&messageId=${data.messageId}`;
    },
    [apiConfig]
  );

  // Load reactions on mount
  useEffect(() => {
    if (!messageId) return;

    const fetchReactions = async () => {
      setIsLoading(true);
      try {
        const url = buildGetUrl(messageId);
        const response = await fetch(url, { credentials: 'include', headers: { ...extraHeaders } });
        if (response.ok) {
          const rawData = await response.json();
          const data = apiConfig.parseGetResponse
            ? apiConfig.parseGetResponse(rawData)
            : (rawData as { reactions: MessageReaction[] });

          if (data?.reactions) {
            setReactions(data.reactions);
          }
        }
      } catch (error) {
        console.error("Failed to fetch reactions:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReactions();
  }, [messageId, buildGetUrl, apiConfig]);

  const addReaction = useCallback(
    async (reaction: MessageReaction) => {
      // // Optimistic update
      // const newReaction: MessageReaction = {
      //   emoji,
      //   participantId,
      //   messageId,
      // };

      setReactions((prev) => [...prev, reaction]);

      try {
        const requestData: ReactionRequestData = {
          ...reaction,
          threadId,
        };

        console.log("useReactions: requestData:", requestData);

        const { url, options } = buildAddRequest(requestData);
        const response = await fetch(url, { ...options, credentials: 'include', headers: { ...options.headers, ...extraHeaders } });

        if (!response.ok) {
          throw new Error("Failed to add reaction");
        }
      } catch (error) {
        console.error("Failed to add reaction:", error);
        // Revert optimistic update
        setReactions((prev) =>
          prev.filter(
            (r) =>
              !(
                r.emoji === reaction.emoji &&
                r.participantId === reaction.participantId &&
                r.messageId === reaction.messageId
              )
          )
        );
      }
    },
    [messageId, participantId, threadId, buildAddRequest]
  );

  const removeReaction = useCallback(
    async (emoji: string) => {
      // Optimistic update
      setReactions((prev) =>
        prev.filter(
          (r) =>
            !(
              r.emoji === emoji &&
              r.participantId === participantId &&
              r.messageId === messageId
            )
        )
      );

      try {
        const requestData: ReactionRequestData = {
          threadId,
          participantId,
          messageId,
          emoji, // Note: emoji might not be needed for delete URL depending on API, but good to have in data struct
        };

        const url = buildRemoveUrl(requestData);
        const response = await fetch(url, { method: "DELETE", credentials: 'include', headers: { ...extraHeaders } });

        if (!response.ok) {
          throw new Error("Failed to remove reaction");
        }
      } catch (error) {
        console.error("Failed to remove reaction:", error);
        // Revert optimistic update
        const restoredReaction: MessageReaction = {
          emoji,
          participantId,
          messageId,
        };
        setReactions((prev) => [...prev, restoredReaction]);
      }
    },
    [messageId, participantId, buildRemoveUrl]
  );

  return {
    reactions,
    isLoading,
    addReaction,
    removeReaction,
  };
}

// Preset configurations

export const createLocalReactionsConfig = (
  baseUrl: string
): ReactionsApiConfig => ({
  getEndpoint: `${baseUrl}/api/message/reactions`,
  addEndpoint: `${baseUrl}/api/message/reaction`,
  removeEndpoint: `${baseUrl}/api/message/reaction`,
});
